import React from 'react';

import {
  type CaraWorkspaceFieldsFragment,
  type HtmlDocumentTemplateFieldsFragment
} from '@oysterjs/ais/api/graphql';

import {
  ApplicationFileArtifact,
  ArtifactPlaceholder,
  DocumentDraftArtifact,
  DocxEditorArtifact,
  XlsxEditorArtifact,
  EmailDraftArtifact,
  FileEditorArtifact,
  FillTemplateArtifact,
  OutboundCallDraftArtifact,
  PDFEditorArtifact
} from './artifact-cards';
import { Badge } from '@oysterjs/ui/badge';
import { draftEmailSummary } from '@oysterjs/ais/components/workspaces/lib/draft-email-event';
import { newestArtifactVersion } from '@oysterjs/ais/components/workspaces/lib/artifact';
import { openedDocumentVersion } from '@oysterjs/ais/components/documents/document-versions';
import {
  applicationFileGeneratingLabel,
  docxEditorToolTitle,
  findCustomFormName,
  isDocxApplicationFile,
  pdfEditorFillTitle
} from '@oysterjs/ais/components/workspaces/lib/utils';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];
type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * Renderers for `'artifact'`-classified stage events (per `getStageEventKind`).
 * `'row'`-classified events are rendered by `StageEventGroup` from the data
 * extracted by `lib/step-title.ts` + `lib/step-content.ts` and never reach
 * this file. `'hidden'`-classified events are filtered out upstream.
 */

export const renderDraftOutboundCallEvent = (event: StageEvent, workspaceId: string) => {
  if (!event.toolCall?.draftOutboundCallTool?.input) {
    return (
      <ArtifactPlaceholder
        title="Outbound Call"
        icon={<></>}
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Drafting call
          </Badge>
        }
      />
    );
  }
  return (
    <OutboundCallDraftArtifact
      draft={event.toolCall.draftOutboundCallTool.input}
      output={event.toolCall.draftOutboundCallTool.output}
      completed={!!event.endedAt}
      workspaceId={workspaceId}
    />
  );
};

export const renderDraftEmailEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const draft = draftEmailSummary(event);
  const operation = event.toolCall?.draftEmailTool?.input?.operation;
  if (!draft) {
    return (
      <ArtifactPlaceholder
        title="(Empty subject)"
        loadingBadge={
          <Badge color="blue" className="mb-4">
            {operation === 'UPDATE_DRAFT' ? 'Updating email' : 'Planning email'}
          </Badge>
        }
      />
    );
  }

  // Pin the card to the version this call wrote, and open that version.
  // Display comes from the tool call (see versionDraftEmail).
  const outputVersion = event.toolCall?.draftEmailTool?.output?.version;
  const artifact =
    draft.artifactId && typeof outputVersion === 'number' && outputVersion > 0
      ? workspace.artifacts.find((a) => a.id === draft.artifactId && a.version === outputVersion)
      : draft.artifactId
        ? findArtifactForEvent(workspace, draft.artifactId, event.startedAt)
        : undefined;

  return (
    <EmailDraftArtifact
      draft={draft}
      artifact={artifact}
      version={typeof outputVersion === 'number' && outputVersion > 0 ? outputVersion : undefined}
      completed={!!event.endedAt}
      updating={operation === 'UPDATE_DRAFT'}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

export const renderDraftDocumentEvent = (
  event: StageEvent,
  workspaceId: string,
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[]
) => {
  if (!event.toolCall?.draftDocumentTool?.input) {
    return (
      <ArtifactPlaceholder
        title="(Untitled document)"
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Planning document
          </Badge>
        }
      />
    );
  }
  return (
    <DocumentDraftArtifact
      workspaceId={workspaceId}
      document={event.toolCall.draftDocumentTool.input}
      htmlDocumentTemplates={htmlDocumentTemplates}
      completed={!!event.endedAt}
    />
  );
};

export const renderFillTemplateEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  if (!event.toolCall?.fillTemplateTool?.input) {
    return (
      <ArtifactPlaceholder
        title=""
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Filling template
          </Badge>
        }
      />
    );
  }

  const artifactId = event.toolCall?.fillTemplateTool?.output?.artifactId;

  const artifact = artifactId
    ? workspace.artifacts
        .filter((a) => a.id === artifactId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .find((a) => new Date(a.createdAt).getTime() >= new Date(event.startedAt).getTime())
    : undefined;

  const finalArtifact =
    artifact ||
    (artifactId
      ? workspace.artifacts
          .filter((a) => a.id === artifactId)
          .sort((a, b) => b.version - a.version)[0]
      : undefined);

  return (
    <FillTemplateArtifact
      workspaceId={workspace.id}
      toolCall={event.toolCall.fillTemplateTool}
      artifact={finalArtifact}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

// GENERATE and REVISE send a brief and a separate writer produces the document, so the brief's
// length says nothing about it.
const isDocumentWriterCall = (event: StageEvent): boolean => {
  const operation = event.toolCall?.fileEditorTool?.input?.operation;
  return operation === 'GENERATE' || operation === 'REVISE';
};

const fileEditorContentLength = (event: StageEvent, artifact: Artifact | undefined): number => {
  const input = event.toolCall?.fileEditorTool?.input;
  if (artifact?.document && isDocumentWriterCall(event)) {
    return artifact.document.content.length;
  }
  return (
    (input?.content?.length || 0) +
    (input?.replaceText?.length || 0) +
    // GENERATE sends a brief rather than markup, so content stays empty for the whole call and
    // the card would stay on "Planning document".
    (input?.requirements?.length || 0)
  );
};

export const renderFileEditorEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
): React.ReactElement | null => {
  // `getStageEventKind` already filters out errors, READ ops, and intermediate
  // events in a multi-event chain, so we don't need to re-check those here.
  const artifact = workspace.artifacts.filter(
    (a) =>
      a.id === event.toolCall?.fileEditorTool?.output?.fileId &&
      a.version === event.toolCall?.fileEditorTool?.output?.version
  )?.[0];

  return (
    <FileEditorArtifact
      artifact={artifact}
      title={event.toolCall?.fileEditorTool?.input?.title}
      contentLength={fileEditorContentLength(event, artifact)}
      awaitingDocument={!artifact && isDocumentWriterCall(event)}
      openedArtifact={
        artifact &&
        openedDocumentVersion(
          workspace.artifacts.filter((each) => each.id === artifact.id),
          artifact
        )
      }
      latestArtifact={artifact && newestArtifactVersion(workspace.artifacts, artifact.id)}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

const renderPdfEditorFillEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const artifactRef = event.toolCall?.pdfEditorTool?.output?.artifact;
  const artifactId = artifactRef?.id;
  const version = artifactRef?.version;
  const artifact = artifactId
    ? version != null
      ? workspace.artifacts.find((a) => a.id === artifactId && a.version === version)
      : workspace.artifacts
          .filter((a) => a.id === artifactId)
          .sort((a, b) => b.version - a.version)[0]
    : undefined;
  // A FILL result names its form only by ID, so fall back to the form name recorded by the
  // LIST / DESCRIBE calls that preceded it in this workspace.
  const formName =
    event.toolCall?.pdfEditorTool?.output?.form?.name?.trim() ||
    artifact?.customForm?.customForm?.title?.trim() ||
    findCustomFormName(workspace, event.toolCall?.pdfEditorTool?.input?.formId);
  return (
    <PDFEditorArtifact
      artifact={artifact}
      fileName={pdfEditorFillTitle({ artifact, formName })}
      formTitle={formName}
      subtitle={workspace.metadata?.clientName || undefined}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

const renderPdfEditorFillFormFieldsEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const output = event.toolCall?.pdfEditorTool?.output as
    | { artifactId?: string | null; fileId?: string | null; version?: number | null }
    | undefined;
  const matchId = output?.artifactId || output?.fileId;
  const version = output?.version;

  const artifact =
    version !== undefined && version !== null
      ? workspace.artifacts.find((a) => a.id === matchId && a.version === version)
      : workspace.artifacts
          .filter((a) => a.id === matchId)
          .sort((a, b) => b.version - a.version)[0];

  return (
    <PDFEditorArtifact
      artifact={artifact}
      fileName={pdfEditorFillTitle({
        artifact,
        inputFileName: event.toolCall?.pdfEditorTool?.input?.inputFiles?.[0]?.fileName
      })}
      subtitle={workspace.metadata?.clientName || undefined}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

export const renderPdfEditorEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
): React.ReactElement | null => {
  // `getStageEventKind` only routes successful FILL / FILL_FORM_FIELDS events to
  // this dispatcher; LIST/DESCRIBE/INSPECT_FORM_FIELDS render as rows in the
  // stage-event group, and erroring fills are hidden or rolled into a row.
  const operation = event.toolCall?.pdfEditorTool?.input?.operation;
  if (operation === 'FILL') {
    return renderPdfEditorFillEvent(event, workspace, setShowArtifactFocusDialog);
  }
  if (operation === 'FILL_FORM_FIELDS') {
    return renderPdfEditorFillFormFieldsEvent(event, workspace, setShowArtifactFocusDialog);
  }
  return null;
};

export const renderApplicationFileGeneratorEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const appFileTool = event.toolCall?.applicationFileGeneratorTool;

  if (!appFileTool?.input?.fileType) {
    return (
      <ArtifactPlaceholder
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Drafting document
          </Badge>
        }
        title="(Untitled document)"
      />
    );
  }

  if (!appFileTool?.output?.fileId) {
    return (
      <ArtifactPlaceholder
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Drafting{' '}
            {applicationFileGeneratingLabel(
              isDocxApplicationFile({ toolCall: appFileTool ?? undefined }),
              false
            )}
          </Badge>
        }
        title={appFileTool.input.title || '(Untitled document)'}
      />
    );
  }

  const fileId = appFileTool.output.fileId;

  const outputVersion = appFileTool.output.version;
  const hasOutputVersion = typeof outputVersion === 'number' && outputVersion > 0;

  // Prefer the exact artifact version named by the tool result. Timestamp
  // matching preserves compatibility with older events that did not persist
  // a reliable output version.
  const exactArtifact =
    fileId && hasOutputVersion
      ? workspace.artifacts.find((a) => a.id === fileId && a.version === outputVersion)
      : undefined;
  const timestampArtifact =
    fileId && !hasOutputVersion
      ? workspace.artifacts
          .filter((a) => a.id === fileId)
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .find((a) => new Date(a.createdAt).getTime() >= new Date(event.startedAt).getTime())
      : undefined;

  const finalArtifact =
    exactArtifact ||
    timestampArtifact ||
    (fileId && !hasOutputVersion
      ? workspace.artifacts.filter((a) => a.id === fileId).sort((a, b) => b.version - a.version)[0]
      : undefined);

  return (
    <ApplicationFileArtifact
      workspaceId={workspace.id}
      artifact={finalArtifact}
      toolCall={appFileTool}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

/**
 * Resolve the artifact version an event produced. A tool call can create several versions
 * of the same artifact over a stage, so we take the first version created at or after the
 * event started rather than trusting the version reported in the tool output. When no
 * version matches the timestamp (clock skew, or an artifact written before the event was
 * recorded) we fall back to the latest version so the card still renders.
 */
const findArtifactForEvent = (
  workspace: CaraWorkspaceFieldsFragment,
  artifactId: string,
  eventStartedAt: Date | string
): Artifact | undefined => {
  const versions = workspace.artifacts.filter((a) => a.id === artifactId);
  const started = new Date(eventStartedAt).getTime();
  const produced = [...versions]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .find((a) => new Date(a.createdAt).getTime() >= started);
  return produced ?? [...versions].sort((a, b) => b.version - a.version)[0];
};

export const renderDocxEditorEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const docxTool = event.toolCall?.docxEditorTool;

  if (!docxTool?.input?.operation) {
    return (
      <ArtifactPlaceholder
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Drafting document
          </Badge>
        }
        title="(Untitled document)"
      />
    );
  }

  if (['INSPECT_ARTIFACT', 'INSPECT_UPLOADED'].includes(docxTool?.input?.operation)) {
    return null;
  }

  if (!docxTool?.output?.artifactId) {
    return (
      <ArtifactPlaceholder
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Drafting {applicationFileGeneratingLabel(true, false)}
          </Badge>
        }
        title={docxEditorToolTitle(docxTool) || '(Untitled document)'}
      />
    );
  }

  return (
    <DocxEditorArtifact
      workspaceId={workspace.id}
      artifact={findArtifactForEvent(workspace, docxTool.output.artifactId, event.startedAt)}
      toolCall={docxTool}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};

export const renderXlsxEditorEvent = (
  event: StageEvent,
  workspace: CaraWorkspaceFieldsFragment,
  setShowArtifactFocusDialog: (artifact: Artifact) => void
) => {
  const xlsxTool = event.toolCall?.xlsxEditorTool;
  const title = xlsxTool?.input?.title?.trim();

  if (!xlsxTool?.output?.artifactId) {
    return (
      <ArtifactPlaceholder
        loadingBadge={
          <Badge color="blue" className="mb-4">
            Filling {applicationFileGeneratingLabel(false, false)}
          </Badge>
        }
        title={title || '(Untitled spreadsheet)'}
      />
    );
  }

  return (
    <XlsxEditorArtifact
      artifact={findArtifactForEvent(workspace, xlsxTool.output.artifactId, event.startedAt)}
      toolCall={xlsxTool}
      completed={!!event.endedAt}
      setShowArtifactFocusDialog={setShowArtifactFocusDialog}
    />
  );
};
